import React, { useState, useEffect } from 'react';
import { CurrencyEnum, IPlanPricing } from '../../../../interfaces/plan.interface';
import { SUPPORTED_CURRENCIES, formatCurrency } from '../../../../config/currencies';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';

interface MultiCurrencyPricingProps {
  pricing: IPlanPricing[];
  onChange: (pricing: IPlanPricing[]) => void;
  isEditMode?: boolean;
}

const MultiCurrencyPricing: React.FC<MultiCurrencyPricingProps> = ({
  pricing,
  onChange,
  isEditMode = false
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingPricing, setEditingPricing] = useState<IPlanPricing | null>(null);

  // Initialize with default currencies if no pricing exists
  useEffect(() => {
    if (!pricing || pricing.length === 0) {
      const defaultPricing: IPlanPricing[] = [
        {
          currency: CurrencyEnum.USD,
          cost: 0,
          stripe_plan_id: '',
          isDefault: true
        },
        {
          currency: CurrencyEnum.EUR,
          cost: 0,
          stripe_plan_id: '',
          isDefault: false
        },
        {
          currency: CurrencyEnum.GBP,
          cost: 0,
          stripe_plan_id: '',
          isDefault: false
        }
      ];
      onChange(defaultPricing);
    }
  }, []);

  const addCurrency = () => {
    const availableCurrencies = Object.values(CurrencyEnum).filter(
      currency => !pricing.some(p => p.currency === currency)
    );

    if (availableCurrencies.length > 0) {
      const newPricing: IPlanPricing = {
        currency: availableCurrencies[0],
        cost: 0,
        stripe_plan_id: '',
        isDefault: false
      };
      onChange([...pricing, newPricing]);
    }
  };

  const removeCurrency = (index: number) => {
    const newPricing = pricing.filter((_, i) => i !== index);
    
    // Ensure at least one currency remains
    if (newPricing.length === 0) {
      newPricing.push({
        currency: CurrencyEnum.USD,
        cost: 0,
        stripe_plan_id: '',
        isDefault: true
      });
    }
    
    // If we removed the default currency, make the first one default
    if (newPricing.length > 0 && !newPricing.some(p => p.isDefault)) {
      newPricing[0].isDefault = true;
    }
    
    onChange(newPricing);
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditingPricing({ ...pricing[index] });
  };

  const saveEditing = () => {
    if (editingPricing && editingIndex !== null) {
      const newPricing = [...pricing];
      newPricing[editingIndex] = editingPricing;
      onChange(newPricing);
      setEditingIndex(null);
      setEditingPricing(null);
    }
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditingPricing(null);
  };

  const updatePricing = (index: number, field: keyof IPlanPricing, value: any) => {
    const newPricing = [...pricing];
    newPricing[index] = { ...newPricing[index], [field]: value };
    onChange(newPricing);
  };

  const setDefaultCurrency = (index: number) => {
    const newPricing = pricing.map((p, i) => ({
      ...p,
      isDefault: i === index
    }));
    onChange(newPricing);
  };

  const getAvailableCurrencies = (currentCurrency: CurrencyEnum) => {
    return Object.values(CurrencyEnum).filter(
      currency => currency === currentCurrency || !pricing.some(p => p.currency === currency)
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-700">Multi-Currency Pricing</h3>
        {/*<button*/}
        {/*  type="button"*/}
        {/*  onClick={addCurrency}*/}
        {/*  className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"*/}
        {/*>*/}
        {/*  <FontAwesomeIcon icon={faPlus} />*/}
        {/*  Add Currency*/}
        {/*</button>*/}
      </div>

      <div className="space-y-3">
        {pricing.map((price, index) => (
          <div key={index} className="border rounded-lg p-4 bg-gray-50">
            {editingIndex === index ? (
              // Edit Mode
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency
                    </label>
                    <select
                      value={editingPricing?.currency || CurrencyEnum.USD}
                      onChange={(e) => setEditingPricing(prev => prev ? {
                        ...prev,
                        currency: e.target.value as CurrencyEnum
                      } : null)}
                      className="w-full p-2 border rounded-md"
                    >
                      {getAvailableCurrencies(price.currency).map(currency => (
                        <option key={currency} value={currency}>
                          {SUPPORTED_CURRENCIES[currency].name} ({SUPPORTED_CURRENCIES[currency].symbol})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cost
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingPricing?.cost || 0}
                      onChange={(e) => setEditingPricing(prev => prev ? {
                        ...prev,
                        cost: parseFloat(e.target.value) || 0
                      } : null)}
                      className="w-full p-2 border rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stripe Price ID
                    </label>
                    <input
                      type="text"
                      value={editingPricing?.stripe_plan_id || ''}
                      onChange={(e) => setEditingPricing(prev => prev ? {
                        ...prev,
                        stripe_plan_id: e.target.value
                      } : null)}
                      className="w-full p-2 border rounded-md"
                      placeholder="price_..."
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`default-${index}`}
                    checked={editingPricing?.isDefault || false}
                    onChange={(e) => setEditingPricing(prev => prev ? {
                      ...prev,
                      isDefault: e.target.checked
                    } : null)}
                    className="rounded"
                  />
                  <label htmlFor={`default-${index}`} className="text-sm text-gray-700">
                    Set as default currency
                  </label>
                </div>
                
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={saveEditing}
                    className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // Display Mode
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold">
                      {SUPPORTED_CURRENCIES[price.currency].symbol}
                    </span>
                    <span className="text-sm text-gray-600">
                      {SUPPORTED_CURRENCIES[price.currency].name}
                    </span>
                    {price.isDefault && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                        Default
                      </span>
                    )}
                  </div>
                  
                  <div className="text-lg font-bold">
                    {formatCurrency(price.cost, price.currency)}
                  </div>
                  
                  {price.stripe_plan_id && (
                    <div className="text-sm text-gray-500">
                      ID: {price.stripe_plan_id.substring(0, 20)}...
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => startEditing(index)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                    title="Edit"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  
                  {/*{pricing.length > 1 && (*/}
                  {/*  <button*/}
                  {/*    type="button"*/}
                  {/*    onClick={() => removeCurrency(index)}*/}
                  {/*    className="p-2 text-red-600 hover:bg-red-100 rounded"*/}
                  {/*    title="Remove"*/}
                  {/*  >*/}
                  {/*    <FontAwesomeIcon icon={faTrash} />*/}
                  {/*  </button>*/}
                  {/*)}*/}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {pricing.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No currencies configured. Click "Add Currency" to get started.
        </div>
      )}
    </div>
  );
};

export default MultiCurrencyPricing; 